import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { IepFormContext } from '../../../../utils/iepFormProvider'
import { useQuery } from '@apollo/client'
import { QUERY_STUDENT_COMMON_SIS } from '../../../../operations/queries/forms/SIS'
import { QUERY_FORM3454R_VERSION } from '../../../../operations/queries/forms/3454R'
import dayjs from 'dayjs'
import FormErrorProvider, { FormErrorContext } from '../errorProvider'
import FormContainer from '../../../FormContainer'
import { Alert, Card, DatePicker, Form, Input, Radio, Typography } from 'antd'
import FormCollaboration from '../../../FormCollaboration'
import Title from 'antd/lib/typography/Title'
import StudentFullNameSIS from '../sis/StudentFullNameSIS'
import FormItem from 'antd/lib/form/FormItem'
import StudentFormActions from '../StudentFormActions'
import { PRINT_FORM3454R } from '../../../../operations/mutations/forms/3454R'
import Editor from '../../../elements/common/Editor'

const { Paragraph } = Typography

const FormRecord = ({
  data,
  studentFormId,
  studentId,
  formId,
  schoolId,
  studentMeetingId,
  sis,
  versionId
}) => {
  const [form] = Form.useForm()

  const { itemErrors, formError } = useContext(FormErrorContext)

  const getFormFieldValues = () => {
    const formFieldValues = form.getFieldsValue()
    return {
      ...formFieldValues,
      studentFormId: studentFormId,
      formId: formId,
      studentId: studentId,
      schoolId: schoolId,
      studentMeetingId: studentMeetingId
    }
  }

  const formatFormFieldUpdate = (fieldName, fieldValue) => {
    const update = Object.assign({})
    switch (fieldName) {
      case 'formDate':
        update[fieldName] = fieldValue ? dayjs(fieldValue) : ''
        break
      case 'causeDisability':
        update[fieldName] = fieldValue.toString()
        break
      case 'causeDistrict':
        update[fieldName] = fieldValue.toString()
        break
      case 'causeBehavior':
        update[fieldName] = fieldValue.toString()
        break

      default:
        update[fieldName] = fieldValue
    }
    return update
  }

  const handleFieldCollaborationUpdate = (fieldName, fieldValue) => {
    const update = formatFormFieldUpdate(fieldName, fieldValue)
    form.setFieldsValue(update)
  }

  return (
    <>
      <FormCollaboration
        form={form}
        itemErrors={itemErrors}
        studentFormId={studentFormId}
        handleUpdate={handleFieldCollaborationUpdate}
      >
        {({
          handleMouseEvents,
          handleFocusEvents,
          handleEditorEvents,
          formDisabledState,
          canSave,
          handleActionEvents,
          validationProps
        }) => (
          <Form
            form={form}
            id='studentForm3454R'
            name='studentForm3454R'
            initialValues={data}
            preserve
            layout='vertical'
            className='page-padding-leftRight page-padding-topBottom form-labels-bold'
          >
            <Title level={4} className='center'>
              EDUCATIONAL SERVICES AND PLACEMENT
            </Title>
            <Form.Item
              label='Form Created Date'
              name='formDate'
              {...validationProps('formDate')}
            >
              <DatePicker
                {...handleFocusEvents}
                disabled={formDisabledState.formDate}
                format={[
                  'MM-DD-YYYY',
                  'MM/DD/YYYY',
                  'MM.DD.YY',
                  'M-D-YYYY',
                  'M/D/YYYY',
                  'M.D.YYYY',
                  'MM.DD.YYYY',
                  'MM-DD-YY',
                  'MM/DD/YY',
                  'M-D-YY',
                  'M/D/YY',
                  'M.D.YY',
                  'MMDDYYYY',
                  'MMDDYY'
                ]}
              />
            </Form.Item>

            <Card
              title='Student SIS Data'
              style={{
                marginBottom: 30
              }}
            >
              <Alert
                message='SIS fields are locked, please contact administrator in case of error.'
                type='warning'
                style={{ marginBottom: 20 }}
              />
              <StudentFullNameSIS
                form={form}
                sis={sis}
                id={studentId}
                initialValue={data.studentFullName}
                error={itemErrors.studentFullName}
              />
            </Card>

            <Card
              title='Manifestation Determination (As Appropriate)'
              style={{
                marginBottom: 30
              }}
            >
              <Form.Item
                label='Student’s disability'
                name='studentDisability'
                {...validationProps('studentDisability')}
              >
                <Input
                  {...handleFocusEvents}
                  disabled={formDisabledState.studentDisability}
                />
              </Form.Item>

              <Form.Item
                label='Incident(s) that Resulted in Disciplinary Action'
                name='disabilityIncidents'
                {...validationProps('disabilityIncidents')}
              >
                <Editor
                  formField='disabilityIncidents'
                  disabled={formDisabledState.disabilityIncidents}
                  {...handleEditorEvents}
                />
              </Form.Item>

              <FormItem
                label='The Student’s IEP and Placement'
                name='studentIEPandPlacement'
                {...validationProps('studentIEPandPlacement')}
              >
                <Editor
                  formField='studentIEPandPlacement'
                  disabled={formDisabledState.studentIEPandPlacement}
                  {...handleEditorEvents}
                />
              </FormItem>

              <FormItem
                label='Observations of the Student'
                name='studentObservations'
                {...validationProps('studentObservations')}
              >
                <Editor
                  formField='studentObservations'
                  disabled={formDisabledState.studentObservations}
                  {...handleEditorEvents}
                />
              </FormItem>

              <FormItem
                label='Information provided by the Parents'
                name='parentProvidedInformation'
                {...validationProps('parentProvidedInformation')}
              >
                <Editor
                  formField='parentProvidedInformation'
                  disabled={formDisabledState.parentProvidedInformation}
                  {...handleEditorEvents}
                />
              </FormItem>

              <FormItem
                label='The conduct was caused by or had a direct and substantial relationship to the student’s disability'
                name='causeDisability'
                {...validationProps('causeDisability')}
              >
                <Radio.Group
                  defaultValue={data.causeDisability}
                  onChange={e => {
                    handleMouseEvents.onChange(e)
                  }}
                  buttonStyle='solid'
                  disabled={formDisabledState.causeDisability}
                  name='causeDisability'
                >
                  <Radio.Button value='true'>Yes</Radio.Button>
                  <Radio.Button value='false'>No</Radio.Button>
                </Radio.Group>
              </FormItem>

              <FormItem
                label='The conduct was the direct result of the school district’s failure to implement the IEP.'
                name='causeDistrict'
                {...validationProps('causeDistrict')}
              >
                <Radio.Group
                  defaultValue={data.causeDistrict}
                  onChange={e => {
                    handleMouseEvents.onChange(e)
                  }}
                  buttonStyle='solid'
                  disabled={formDisabledState.causeDistrict}
                  name='causeDistrict'
                >
                  <Radio.Button value='true'>Yes</Radio.Button>
                  <Radio.Button value='false'>No</Radio.Button>
                </Radio.Group>
              </FormItem>

              <FormItem
                label='The student’s behavior was a manifestation of her/his disability.'
                name='causeBehavior'
                {...validationProps('causeBehavior')}
              >
                <Radio.Group
                  defaultValue={data.causeBehavior}
                  onChange={e => {
                    handleMouseEvents.onChange(e)
                  }}
                  buttonStyle='solid'
                  disabled={formDisabledState.causeBehavior}
                  name='causeBehavior'
                >
                  <Radio.Button value='true'>Yes</Radio.Button>
                  <Radio.Button value='false'>No</Radio.Button>
                </Radio.Group>
              </FormItem>
            </Card>

            {formError && <Paragraph type='danger'>{formError}</Paragraph>}
            <StudentFormActions
              query={PRINT_FORM3454R}
              extractDataProp='printStudentForm3454R'
              canSave={canSave}
              handleActionEvents={handleActionEvents}

              getFormFieldValues={getFormFieldValues}
            />
          </Form>
        )}
      </FormCollaboration>
    </>
  )
}

FormRecord.propTypes = {
  data: PropTypes.object.isRequired,
  studentFormId: PropTypes.object.isRequired,
  studentId: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  schoolId: PropTypes.string.isRequired,
  studentMeetingId: PropTypes.string.isRequired,
  sis: PropTypes.object.isRequired,
  versionId: PropTypes.string.isRequired
}

const StudentForm3454R = () => {
  // get current form information
  const { currentStudentInfo, currentEditableForm, studentMeetingId } =
    useContext(IepFormContext)
  const { studentId, schoolId } = currentStudentInfo
  const { studentFormId, formId, versionId } = currentEditableForm

  // Load SIS Data / Form Version Data
  const { loading: studentLoading, data: studentData } = useQuery(
    QUERY_STUDENT_COMMON_SIS,
    { variables: { id: studentId } }
  )
  const { loading: formLoading, data: formData } = useQuery(
    QUERY_FORM3454R_VERSION,
    {
      variables: {
        studentFormId: studentFormId,
        studentId: studentId,
        schoolId: schoolId,
        formId: formId,
        studentMeetingId: studentMeetingId
      }
    }
  )

  const createInitialFormData = (studentInfo, versionInfo) => {
    let initialValues = {}

    if (!studentInfo || !versionInfo) return initialValues

    const { studentForm3454R: form } = versionInfo
    const { student } = studentInfo
    const { formDate, studentFullName } = form

    initialValues = {
      ...form,
      formDate: formDate ? dayjs(formDate) : dayjs(Date.now()),
      studentFullName: studentFullName || student.fullName || ''
    }

    return initialValues
  }
  return (
    <FormErrorProvider>
      <FormContainer
        loading={studentLoading || formLoading}
        form={
          <FormRecord
            data={createInitialFormData(studentData, formData)}
            studentFormId={studentFormId}
            studentId={studentId}
            formId={formId}
            schoolId={schoolId}
            studentMeetingId={studentMeetingId}
            sis={studentData}
            versionId={studentFormId}
          />
        }
      />
    </FormErrorProvider>
  )
}
export default StudentForm3454R
